// src/utils/sse.ts
import { fetchEventSource, EventSourceMessage } from '@microsoft/fetch-event-source';
import { Subject } from 'rxjs';
import { API_BASE_URL } from '../config/constants';

export interface SseEvent {
  type: string;
  data: any;
}

// スケジュールイベント用のインターフェース
export interface ScheduleEventData {
  id: number;
  employeeId: number;
  startTime: string;
  endTime: string;
  title?: string;
  task?: string;
  // その他のスケジュールに関連するプロパティ
}

export class SseClient {
  private eventSubject = new Subject<SseEvent>();
  private controller: AbortController | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private connected = false;

  constructor(private baseUrl: string) {}

  // SSE接続を開始
  connect(): Subject<SseEvent> {
    // 既に接続されている場合は既存のSubjectを返す
    if (this.connected && this.controller) {
      return this.eventSubject;
    }
    
    // 既存の接続を閉じる
    this.disconnect();
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('認証トークンがありません');
      this.eventSubject.next({
        type: 'error',
        data: { message: '認証トークンがありません' }
      });
      return this.eventSubject;
    }

    // AbortControllerを作成（接続のキャンセル用）
    this.controller = new AbortController();

    // fetchEventSourceを使って接続
    this.establishConnection(token);
    
    this.connected = true;
    return this.eventSubject;
  }

  // 接続確立処理
  private async establishConnection(token: string): Promise<void> {
    try {
      await fetchEventSource(`${this.baseUrl}/sse/connect`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: this.controller?.signal,
        
        // 接続が開かれたとき
        onopen: async (response) => {
          if (response.ok) {
            console.log('SSE接続が確立されました');
            this.reconnectAttempts = 0;
          } else {
            console.error(`SSEサーバーエラー: ${response.status} ${response.statusText}`);
            if (response.status === 401 || response.status === 403) {
              // 認証エラー - 再接続しない
              throw new Error('認証エラー');
            }
            // その他のエラーは再接続を試みる
            throw new Error(`サーバーエラー: ${response.status}`);
          }
        },
        
        // メッセージ受信時
        onmessage: (event: EventSourceMessage) => {
          try {
            // デバッグ出力
            console.log(`SSEイベント受信: ${event.event || 'message'}`, event.data);
            
            // イベント種別によって処理分岐
            const eventType = event.event || 'message';
            
            // データをパース
            let parsedData;
            try {
              parsedData = JSON.parse(event.data);
            } catch (e) {
              parsedData = event.data;
            }
            
            // SubjectにSSEイベントを送信
            this.eventSubject.next({
              type: eventType,
              data: parsedData
            });
            
          } catch (error) {
            console.error('SSEイベント処理エラー:', error);
          }
        },
        
        // エラー発生時
        onerror: (err) => {
          console.error('SSE接続エラー:', err);
          
          // 認証エラーは再接続しない
          if (err instanceof Error && err.message.includes('認証エラー')) {
            this.eventSubject.next({
              type: 'error',
              data: { message: '認証エラーが発生しました。再ログインしてください。' }
            });
            return null; // throw errの代わりに false ではなく null または undefined を返す
          }
          
          // 再接続の判断
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.eventSubject.next({
              type: 'error',
              data: { message: 'サーバーとの接続が切断されました。ページを再読み込みしてください。' }
            });
            return null; // throw new Error()の代わりに null または undefined を返す
          }
          
          // 再接続試行回数をインクリメント
          this.reconnectAttempts++;
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
          console.log(`SSE再接続を${delay}ms後に試みます (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          
          return delay; // 指数バックオフで再接続
        }
      });
    } catch (error) {
      console.error('SSE接続処理エラー:', error);
      
      // フェイタルエラーの場合は通知
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.eventSubject.next({
          type: 'error',
          data: { message: 'サーバーとの接続に失敗しました。ネットワーク状態を確認してください。' }
        });
      }
    }
  }
  
  // SSE接続を閉じる
  disconnect(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
      this.connected = false;
      console.log('SSE接続を閉じました');
    }
  }
}

// シングルトンインスタンス
export const sseClient = new SseClient(`${API_BASE_URL}/`);